/**
 * The thread's pending slot (spec 2026-10-18, AI companion growth architecture,
 * 6.2): what the trader's next message answers. CompanionManager holds one per
 * thread; these are its rules as plain functions.
 */
import type { EntityType } from '../tools/tool-manifest'
import type { ChatChoice, Message, Pending, PendingWire } from '../types/companion'

/** A line that opens a list item: "- A", "* A", "+ A", a bullet, "1. A" or "1) A". */
const LIST_ITEM = /^\s*(?:[-*+•]|\d{1,3}[.)])\s+\S/

/** The text less the list it ends with: its items and the blank lines between them. */
function withoutTrailingList(text: string): string {
  const lines = text.trimEnd().split('\n')
  for (let last = lines.pop(); last !== undefined; last = lines.pop()) {
    if (last.trim() && !LIST_ITEM.test(last)) {
      lines.push(last)
      break
    }
  }
  return lines.join('\n')
}

/**
 * An answer that ends by asking the trader something: their next message
 * answers it. The options listed after the question are part of asking it
 * ("Which one?" and then the lists). companion-api has its own copy of this rule.
 */
export function endsWithQuestion(text: string): boolean {
  const asks = (s: string) => /\?\s*$/.test(s.trim())
  return asks(text) || asks(withoutTrailingList(text))
}

/**
 * A "?" that ends a sentence: followed by a space or the end, once closing
 * quotes, brackets and emphasis marks are set aside. One inside a link
 * (https://x.com/a?id=1) is not a question.
 */
const SENTENCE_QUESTION = /\?(?=["'”’)\]*_]*(?:\s|$))/

/**
 * An answer that asks the trader something anywhere in it, not only as its
 * last words ("Which one do you mean? There is no panel on screen."). It reads
 * every answer endsWithQuestion reads, and more.
 */
export function asksQuestion(text: string): boolean {
  return SENTENCE_QUESTION.test(text)
}

/**
 * Who a reply goes back to: whoever asked, when the slot holds a question or
 * choices. An offer (a walkthrough) is not a question; a message typed under
 * one is a new question.
 */
export function askedBy(pending: Pending | null): Pending['from'] | null {
  return pending && (pending.kind === 'question' || pending.kind === 'choices')
    ? pending.from
    : null
}

/** What the servers hear of the slot: who asked and what, never a tour or a clock. */
export function pendingWire(pending: Pending): PendingWire {
  return {
    kind: pending.kind,
    from: pending.from,
    ...(pending.resolves ? { resolves: pending.resolves } : {}),
  }
}

/**
 * The slot an analyst answer leaves. Its choices hold it when the answer asks
 * (anywhere in it): the next reply comes back to the analyst. Choices under an
 * answer that asks nothing ("List my watchlists") show as buttons and hold no
 * slot, so a message typed under them is routed like any other. With no
 * choices, the question the answer ends with holds it.
 */
export function analystPending(
  answer: { text: string; choices?: ChatChoice[]; resolves?: EntityType },
  now = Date.now(),
): Pending | null {
  if (answer.choices?.length && asksQuestion(answer.text)) {
    return {
      kind: 'choices',
      from: 'analyst',
      ...(answer.resolves ? { resolves: answer.resolves } : {}),
      createdAt: now,
    }
  }
  return endsWithQuestion(answer.text)
    ? { kind: 'question', from: 'analyst', createdAt: now }
    : null
}

/**
 * The slot a saved thread left: what its last answer was waiting on. A reply
 * after it was the next question.
 */
export function restoredPending(messages: readonly Message[]): Pending | null {
  const last = messages[messages.length - 1]
  return last?.role === 'assistant' ? (last.pending ?? null) : null
}
